export type SaveBackupTextResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: "cancelled" | string };

interface ShowSaveFilePickerOptions {
  readonly suggestedName?: string;
  readonly types?: ReadonlyArray<{
    readonly description?: string;
    readonly accept: Readonly<Record<string, readonly string[]>>;
  }>;
}

interface WritableFileStreamLike {
  write(data: string | Blob): Promise<void>;
  close(): Promise<void>;
}

interface FileSystemFileHandleLike {
  createWritable(): Promise<WritableFileStreamLike>;
}

type ShowSaveFilePickerFn = (
  options?: ShowSaveFilePickerOptions,
) => Promise<FileSystemFileHandleLike>;

function getShowSaveFilePicker(): ShowSaveFilePickerFn | null {
  if (typeof window === "undefined") return null;
  const candidate = (
    window as unknown as {
      showSaveFilePicker?: unknown;
    }
  ).showSaveFilePicker;
  return typeof candidate === "function"
    ? (candidate as ShowSaveFilePickerFn)
    : null;
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

function reasonFromUnknown(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function triggerAnchorDownload(filename: string, text: string): void {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function saveBackupText(
  filename: string,
  text: string,
): Promise<SaveBackupTextResult> {
  const picker = getShowSaveFilePicker();
  if (picker) {
    try {
      const handle = await picker({
        suggestedName: filename,
        types: [
          {
            description: "HoverIdle Backup",
            accept: { "text/plain": [".txt"] },
          },
        ],
      });
      const writable = await handle.createWritable();
      try {
        await writable.write(text);
      } finally {
        await writable.close();
      }
      return { ok: true };
    } catch (error) {
      if (isAbortError(error)) {
        return { ok: false, reason: "cancelled" };
      }
      return { ok: false, reason: reasonFromUnknown(error) };
    }
  }

  try {
    triggerAnchorDownload(filename, text);
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: reasonFromUnknown(error) };
  }
}
